/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 *
 * Desktop Navigation: outcome-organized Product dropdown, Solutions, Resources.
 * Pricing and About are top-level links.
 */

"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { useNavigationWithProgress } from "@/hooks/utilities/use-navigation-with-progress";
import { isWaitlistMode, openWaitlist } from "@/lib/utils/waitlist-mode";
import { Button } from "@/components/ui/button";
import { useCallback, useRef, useState } from "react";
import {
  ChevronDown,
  ExternalLink,
  Building2,
  Droplets,
  Home,
  Leaf,
  PlugZap,
  Ruler,
  Thermometer,
  Wrench,
  Calculator,
  FileText,
  Briefcase,
  Receipt,
  DollarSign,
  Bot,
  ArrowRight,
  FileSignature,
  CalendarClock,
  BarChart3,
  Users,
  Sunrise,
  FolderOpen,
  CalendarDays,
  ShoppingCart,
  Handshake,
  Globe,
  ShieldCheck,
  Mail,
} from "lucide-react";
import { AI_TOOL_COUNT } from "@/content/landing/constants";

// ---------------------------------------------------------------------------
// Navigation data — organized by outcome, not feature name
// ---------------------------------------------------------------------------

const PRODUCT_GROUPS = [
  {
    label: "Sell & Win",
    items: [
      {
        label: "CRM",
        href: "/features/crm",
        icon: Users,
        description: "Clients, contacts, jobsites, pipeline",
      },
      {
        label: "Estimates & Pricing",
        href: "/features/estimates",
        icon: Calculator,
        description: "Scope, line items, 5 overhead methods",
      },
      {
        label: "Blueprint Takeoffs",
        href: "/features/takeoffs",
        icon: Ruler,
        description: "On-screen measurements, plan sets, work areas",
      },
      {
        label: "Proposals & Portal",
        href: "/features/proposals-portal",
        icon: FileText,
        description: "Branded PDFs, client approval in one click",
      },
      {
        label: "Change Orders",
        href: "/features/change-orders",
        icon: FileSignature,
        description: "First-class COs with client approval",
      },
    ],
  },
  {
    label: "Run Jobs",
    items: [
      {
        label: "Projects & Services",
        href: "/features/project-management",
        icon: Briefcase,
        description: "Tasks, timelines, 4-pill Costs tab",
      },
      {
        label: "Schedule & Dispatch",
        href: "/features/schedule",
        icon: CalendarClock,
        description: "Dispatch, My Day, timelines, service visits",
      },
      {
        label: "Field Ops & Time",
        href: "/features/field-ops",
        icon: Sunrise,
        description: "Mobile clock-in, approvals, crew management",
      },
      {
        label: "Documents",
        href: "/features/documents",
        icon: FolderOpen,
        description: "Polymorphic linking, versioning, receipts",
      },
      {
        label: "Calendar",
        href: "/features/calendar",
        icon: CalendarDays,
        description: "Auto-created deadlines, iCal subscribe",
      },
    ],
  },
  {
    label: "Costs & Cashflow",
    items: [
      {
        label: "Cost Database",
        href: "/features/cost-database",
        icon: DollarSign,
        description: "Materials, labor, equipment, assemblies",
      },
      {
        label: "Budgets & Invoices",
        href: "/features/budgets-invoices",
        icon: Receipt,
        description: "Progress billing, retainage, 3 billing models",
      },
      {
        label: "Purchase Orders",
        href: "/features/purchase-orders",
        icon: ShoppingCart,
        description: "7-state lifecycle, atomic receiving",
      },
      {
        label: "Subcontractors",
        href: "/features/subcontractors",
        icon: Handshake,
        description: "Trade partner directory, performance tracking",
      },
      {
        label: "Client Portal",
        href: "/features/client-portal",
        icon: Globe,
        description: "Share-link access, Stripe invoice payment",
      },
    ],
  },
  {
    label: "Grow & Govern",
    items: [
      {
        label: "Reporting & Analytics",
        href: "/features/reporting",
        icon: BarChart3,
        description: "Budget rollup, variance, pipeline metrics",
      },
      {
        label: "Divisions",
        href: "/features/divisions",
        icon: Building2,
        description: "Multi-division budgets, teams, P&L",
      },
      {
        label: "Team & Permissions",
        href: "/features/collaboration",
        icon: ShieldCheck,
        description: "8 roles, 3-layer tenant isolation",
      },
      {
        label: "Communications",
        href: "/features/email-hub",
        icon: Mail,
        description: "Transactional email, notifications, AI review",
      },
      {
        label: "AI Assistant (Hank)",
        href: "/features/ai-assistant",
        icon: Bot,
        description: `${AI_TOOL_COUNT} tools, plain English, your data`,
      },
    ],
  },
] as const;

const SOLUTION_LINKS = [
  { label: "Concrete", href: "/solutions/concrete", icon: Ruler },
  { label: "Electrical", href: "/solutions/electrical", icon: PlugZap },
  {
    label: "General Contracting",
    href: "/solutions/general-contracting",
    icon: Building2,
  },
  { label: "HVAC", href: "/solutions/hvac", icon: Thermometer },
  { label: "Landscaping", href: "/solutions/landscaping", icon: Leaf },
  { label: "Plumbing", href: "/solutions/plumbing", icon: Droplets },
  {
    label: "Residential",
    href: "/solutions/residential-construction",
    icon: Home,
  },
  { label: "Roofing", href: "/solutions/roofing", icon: Wrench },
] as const;

const RESOURCE_LINKS = [
  { label: "Resource Library", href: "/resources" },
  { label: "Blog", href: "/blog" },
  {
    label: "Docs",
    href: "https://docs.contractor-co-pilot.com",
    external: true,
  },
  { label: "Free Calculators", href: "/tools" },
  { label: "Templates", href: "/templates" },
  { label: "Glossary", href: "/glossary" },
  { label: "Compare CC", href: "/compare" },
] as const;

// ---------------------------------------------------------------------------
// Dropdown component
// ---------------------------------------------------------------------------

function NavDropdown({
  label,
  isActive,
  children,
  wide,
}: {
  label: string;
  isActive: boolean;
  children: React.ReactNode;
  wide?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  const handleEnter = () => {
    clearTimeout(timeoutRef.current);
    setOpen(true);
  };

  const handleLeave = () => {
    timeoutRef.current = setTimeout(() => setOpen(false), 150);
  };

  return (
    <div
      className="relative"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        className={`flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
          isActive
            ? "text-foreground font-semibold"
            : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
        }`}
        aria-expanded={open}
        aria-haspopup="true"
      >
        {label}
        <ChevronDown
          className={`h-3.5 w-3.5 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div
          className={`absolute top-full mt-1 rounded-xl border border-border bg-background shadow-xl py-3 z-50 ${
            wide ? "w-[1040px] -left-[340px]" : "left-0 min-w-[220px]"
          }`}
          role="menu"
        >
          {children}
        </div>
      )}
    </div>
  );
}

function DropdownLink({
  href,
  label,
  external,
  icon: Icon,
  description,
  onClick,
}: {
  href: string;
  label: string;
  external?: boolean;
  icon?: React.ComponentType<{ className?: string }>;
  description?: string;
  onClick?: () => void;
}) {
  const pathname = usePathname();
  const isActive = pathname === href || pathname.startsWith(href + "/");

  if (external) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-3 px-4 py-2.5 text-sm text-muted-foreground hover:text-foreground hover:bg-muted/50 transition-colors"
        role="menuitem"
        onClick={onClick}
      >
        {Icon && <Icon className="h-4 w-4 shrink-0" />}
        <div className="flex-1">
          <span>{label}</span>
        </div>
        <ExternalLink className="h-3 w-3 opacity-50" />
      </a>
    );
  }

  return (
    <Link
      href={href}
      className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors ${
        isActive
          ? "text-foreground font-medium bg-muted/30"
          : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
      }`}
      role="menuitem"
      onClick={onClick}
    >
      {Icon && <Icon className="h-4 w-4 shrink-0 text-secondary" />}
      <div className="flex-1 min-w-0">
        <span className="block font-medium text-foreground">{label}</span>
        {description && (
          <span className="block text-xs text-muted-foreground mt-0.5">
            {description}
          </span>
        )}
      </div>
    </Link>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export function DesktopNavigation() {
  const pathname = usePathname();
  const { router } = useNavigationWithProgress();

  const prefetchRoute = useCallback(
    (href: string) => {
      router.prefetch(href);
    },
    [router],
  );

  const isProductActive =
    pathname === "/features" ||
    pathname.startsWith("/features/") ||
    pathname === "/compare" ||
    pathname.startsWith("/compare/");

  const isSolutionsActive = pathname.startsWith("/solutions");

  const isResourcesActive =
    pathname === "/resources" ||
    pathname.startsWith("/resources/") ||
    pathname === "/tools" ||
    pathname.startsWith("/tools/") ||
    pathname === "/templates" ||
    pathname.startsWith("/templates/") ||
    pathname === "/glossary" ||
    pathname.startsWith("/blog") ||
    pathname === "/compare" ||
    pathname.startsWith("/compare/");

  const isPricingActive = pathname === "/pricing";
  return (
    <>
      <div className="hidden items-center md:flex">
        {/* Product — outcome-organized dropdown */}
        <NavDropdown label="Product" isActive={isProductActive} wide>
          <div className="grid grid-cols-4 gap-1 px-2">
            {PRODUCT_GROUPS.map((group) => (
              <div key={group.label} className="py-1">
                <span className="block px-3 py-2 text-[10px] font-bold text-muted-foreground uppercase tracking-widest">
                  {group.label}
                </span>
                <div className="space-y-0.5">
                  {group.items.map((item) => (
                    <DropdownLink
                      key={item.href}
                      href={item.href}
                      label={item.label}
                      icon={item.icon}
                      description={item.description}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-border mt-2 pt-2 flex items-center gap-6 px-5 py-3">
            <Link
              href="/features"
              className="text-sm font-medium text-secondary hover:text-secondary/80 transition-colors inline-flex items-center gap-1.5"
            >
              All Features
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
            <Link
              href="/compare"
              className="text-sm font-medium text-secondary hover:text-secondary/80 transition-colors inline-flex items-center gap-1.5"
            >
              Compare
              <ArrowRight className="h-3.5 w-3.5" />
            </Link>
          </div>
        </NavDropdown>

        {/* Solutions */}
        <NavDropdown label="Solutions" isActive={isSolutionsActive}>
          <div className="grid grid-cols-2 gap-0 min-w-[340px]">
            {SOLUTION_LINKS.map((link) => (
              <DropdownLink
                key={link.href}
                href={link.href}
                label={link.label}
                icon={link.icon}
              />
            ))}
          </div>
          <div className="border-t border-border mt-1 pt-1">
            <DropdownLink href="/solutions" label="All Solutions" />
          </div>
        </NavDropdown>

        {/* Pricing — top-level link */}
        <Link
          href="/pricing"
          className={`px-3 py-2 text-sm font-medium rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
            isPricingActive
              ? "text-foreground font-semibold"
              : "text-muted-foreground hover:text-foreground hover:bg-muted/50"
          }`}
          onMouseEnter={() => prefetchRoute("/pricing")}
        >
          Pricing
        </Link>

        {/* Resources */}
        <NavDropdown label="Resources" isActive={isResourcesActive}>
          {RESOURCE_LINKS.map((link) => (
            <DropdownLink
              key={link.href}
              href={link.href}
              label={link.label}
              external={"external" in link ? link.external : undefined}
            />
          ))}
        </NavDropdown>
      </div>

      <div className="hidden md:flex md:items-center md:gap-2 md:ml-2">
        <Link
          href="/contact"
          className="px-3 py-2 text-sm font-medium rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-muted-foreground hover:text-foreground hover:bg-muted/50"
        >
          Contact
        </Link>
        {isWaitlistMode() ? (
          <Button
            size="default"
            className="bg-secondary text-white hover:bg-secondary/90 border border-secondary/20 shadow-sm"
            onClick={openWaitlist}
          >
            Join the Waitlist
          </Button>
        ) : (
          <>
            <Link
              href="/auth?mode=signin"
              className="px-3 py-2 text-sm font-medium rounded-md outline-none transition-colors focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-muted-foreground hover:text-foreground hover:bg-muted/50"
              prefetch={true}
              onMouseEnter={() => prefetchRoute("/auth")}
            >
              Sign In
            </Link>
            <Button
              asChild
              size="default"
              className="bg-secondary text-white hover:bg-secondary/90 border border-secondary/20 shadow-sm"
            >
              <Link href="/auth/sign-up">Start Free Trial</Link>
            </Button>
          </>
        )}
      </div>
    </>
  );
}
